import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { ToastProvider } from '@/contexts/ToastContext';
import { ToastContainer } from '@/components/ui/ToastContainer';
import { ErrorBoundary } from '@/components/ui/ErrorBoundary';
import { Layout } from '@/components/layout/Layout';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { AdminRoute } from '@/components/auth/AdminRoute';
import { AnalyticsProvider } from '@/components/analytics/AnalyticsProvider';
import { CookieConsent } from '@/components/ui/CookieConsent';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { HomePage } from '@/pages/HomePage';
import { LoginPage } from '@/pages/LoginPage';
import { CatalogPage } from '@/pages/CatalogPage';
import { ProductDetailPage } from '@/pages/ProductDetailPage';
import { QuotePage } from '@/pages/QuotePage';
import { QuotesListPage } from '@/pages/QuotesListPage';
import { AdminDashboardPage } from '@/pages/admin/AdminDashboardPage';
import { AdminOrdersPage } from '@/pages/admin/AdminOrdersPage';
import { AdminOrderDetailPage } from '@/pages/admin/AdminOrderDetailPage';
import { AdminQuotesPage } from '@/pages/admin/AdminQuotesPage';
import { AdminProductsPage } from '@/pages/admin/AdminProductsPage';
import { AdminProductFormPage } from '@/pages/admin/AdminProductFormPage';
import { AdminMaterialsPage } from '@/pages/admin/AdminMaterialsPage';
import { AdminQueuePage } from '@/pages/admin/AdminQueuePage';
import { AdminAnalyticsPage } from '@/pages/admin/AdminAnalyticsPage';
import { AdminExportsPage } from '@/pages/admin/AdminExportsPage';
import { AdminWebhookErrorsPage } from '@/pages/admin/AdminWebhookErrorsPage';
import { AdminSettingsPage } from '@/pages/admin/AdminSettingsPage';
import { AdminCouponsPage } from '@/pages/admin/AdminCouponsPage';
import { AdminSharedQuotesPage } from '@/pages/admin/AdminSharedQuotesPage';
import { AdminCreateSharedQuotePage } from '@/pages/admin/AdminCreateSharedQuotePage';
import { PublicQuotePage } from '@/pages/PublicQuotePage';
import { PublicQuoteSuccessPage } from '@/pages/PublicQuoteSuccessPage';
import { OrderTrackingPage } from '@/pages/OrderTrackingPage';
import { OrdersPage } from '@/pages/OrdersPage';
import { OrderDetailPage } from '@/pages/OrderDetailPage';
import { CheckoutSuccessPage } from '@/pages/CheckoutSuccessPage';
import { CheckoutCancelPage } from '@/pages/CheckoutCancelPage';
import { AboutPage } from '@/pages/AboutPage';
import { ProfilePage } from '@/pages/ProfilePage';
import { NotFoundPage } from '@/pages/NotFoundPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 30,         // 30s — data refetched on mount when stale
      refetchOnWindowFocus: true,    // pick up changes when user returns to tab
      retry: 1,
    },
  },
});

export const App = () => {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <ToastProvider>
            <BrowserRouter>
            <AnalyticsProvider>
            <Routes>
          <Route element={<Layout />}>
            {/* Public routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/catalog" element={<CatalogPage />} />
            <Route path="/catalog/:id" element={<ProductDetailPage />} />

            {/* Quote routes (authenticated) */}
            <Route
              path="/quote"
              element={
                <ProtectedRoute>
                  <QuotePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/quotes"
              element={
                <ProtectedRoute>
                  <QuotesListPage />
                </ProtectedRoute>
              }
            />

            {/* Order routes (authenticated) */}
            <Route
              path="/orders"
              element={
                <ProtectedRoute>
                  <OrdersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orders/:id"
              element={
                <ProtectedRoute>
                  <OrderDetailPage />
                </ProtectedRoute>
              }
            />

            {/* Profile route (authenticated) */}
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />

            {/* Checkout routes (authenticated) */}
            <Route
              path="/checkout/success"
              element={
                <ProtectedRoute>
                  <CheckoutSuccessPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/checkout/cancel"
              element={
                <ProtectedRoute>
                  <CheckoutCancelPage />
                </ProtectedRoute>
              }
            />

            {/* Admin routes with sidebar layout */}
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <AdminLayout />
                </AdminRoute>
              }
            >
              <Route index element={<AdminDashboardPage />} />
              <Route path="orders" element={<AdminOrdersPage />} />
              <Route path="orders/:id" element={<AdminOrderDetailPage />} />
              <Route path="quotes" element={<AdminQuotesPage />} />
              <Route path="products" element={<AdminProductsPage />} />
              <Route path="products/new" element={<AdminProductFormPage />} />
              <Route path="products/:id/edit" element={<AdminProductFormPage />} />
              <Route path="materials" element={<AdminMaterialsPage />} />
              <Route path="coupons" element={<AdminCouponsPage />} />
              <Route path="queue" element={<AdminQueuePage />} />
              <Route path="analytics" element={<AdminAnalyticsPage />} />
              <Route path="exports" element={<AdminExportsPage />} />
              <Route path="webhook-errors" element={<AdminWebhookErrorsPage />} />
              <Route path="settings" element={<AdminSettingsPage />} />
              <Route path="shared-quotes" element={<AdminSharedQuotesPage />} />
              <Route path="shared-quotes/new" element={<AdminCreateSharedQuotePage />} />
            </Route>

            {/* Public shared quote routes (no auth required) */}
            <Route path="/q/:token" element={<PublicQuotePage />} />
            <Route path="/q/:token/success" element={<PublicQuoteSuccessPage />} />

            {/* Guest order tracking (no auth required) */}
            <Route path="/track-order" element={<OrderTrackingPage />} />

            {/* Catch-all */}
            <Route path="*" element={<NotFoundPage />} />
          </Route>
            </Routes>
            <CookieConsent />
            <ToastContainer />
            </AnalyticsProvider>
            </BrowserRouter>
            </ToastProvider>
          </AuthProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};
